// packages/web-ui/src/components/slash-palette/SlashMenu.tsx
//
// Story W2-S7 AC #7 — inline slash menu anchored above the composer.
//
// Surface contract:
//   [data-testid="slash-menu"] — root container (visible only when `open`)
//   [data-testid="slash-list"] — listbox of commands
//   [data-testid="slash-row"][data-command-id=...] — one per command
//   [data-testid="slash-row"][data-active="true"] — active row
//   [data-testid="slash-parameter-form"] — parameter form when entered
//
// The inline menu does NOT use Radix `Popover` to avoid pulling in extra
// portal machinery for tests; it's a plain absolutely-positioned div above
// the composer textarea. The `<Composer>` host owns the open/close gate.

import type { JSX } from 'react';
import { SlashContent } from './SlashContent.js';
import type { SlashCommand, SlashDispatchContext } from './commands.js';

export interface SlashMenuProps {
  open: boolean;
  /** Substring after the leading `/` in the textarea (e.g. `gen`). */
  query: string;
  ctx: SlashDispatchContext;
  onClose: () => void;
  /** Optional merged command source (static + skill-derived entries). */
  commands?: ReadonlyArray<SlashCommand>;
}

export function SlashMenu(props: SlashMenuProps): JSX.Element | null {
  const { open, query, ctx, onClose, commands } = props;
  if (!open) return null;
  return (
    <div
      data-testid="slash-menu"
      data-state={open ? 'open' : 'closed'}
      role="dialog"
      aria-label="Slash command menu"
      className="border-taupe-500 bg-cream-50 absolute bottom-full left-0 z-50 mb-1 max-h-[420px] overflow-y-auto rounded-none border"
    >
      <SlashContent
        mode="inline"
        query={query}
        ctx={ctx}
        onClose={onClose}
        commands={commands}
      />
    </div>
  );
}
